/**
 * @fileoverview Portal de cobrança (read-only) — aba "Faturamento" em /settings.
 * Consome GET /account/billing (proxy do painel de pedidos, fonte da verdade de
 * billing) e exibe três blocos: Plano atual, Histórico de faturas e Dados de
 * cobrança. Somente leitura no v1 — trocar cartão / cancelar / upgrade entram
 * depois (v2 / E3b). Visível só para admin/super (gate no backend e na aba).
 */

import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, Dialog } from "@material-ui/core";
import {
  ReceiptText,
  UserRound,
  Sparkles,
  RefreshCw,
  AlertCircle,
  ArrowUpCircle,
  CreditCard,
  Ban,
  Loader,
  QrCode,
} from "lucide-react";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UpgradeModal from "../Onboarding/UpgradeModal";
import ChangeCardModal from "../Onboarding/ChangeCardModal";

// ── formatadores ────────────────────────────────────────────────
const fmtMoney = (cents) =>
  typeof cents === "number"
    ? (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const daysLeft = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return Number.isNaN(diff) ? null : diff;
};

// ── mapas de status ─────────────────────────────────────────────
const STATUS_META = {
  teste_gratis: { label: i18n.t("billing.status.teste_gratis"), tone: "warn" },
  pago_ativo: { label: i18n.t("billing.status.pago_ativo"), tone: "ok" },
  aguarda_pagamento: { label: i18n.t("billing.status.aguarda_pagamento"), tone: "warn" },
  aguarda_assinatura: { label: i18n.t("billing.status.aguarda_assinatura"), tone: "warn" },
  pagamento_vencido: { label: i18n.t("billing.status.pagamento_vencido"), tone: "error" },
  bloqueado: { label: i18n.t("billing.status.bloqueado"), tone: "error" },
  cancelado: { label: i18n.t("billing.status.cancelado"), tone: "muted" },
  onboarding_pendente: { label: i18n.t("billing.status.onboarding"), tone: "info" },
  onboarding_finalizado: { label: i18n.t("billing.status.onboarding"), tone: "info" },
};

const INVOICE_META = {
  paga: { label: i18n.t("billing.invoiceStatus.paga"), tone: "ok" },
  pendente: { label: i18n.t("billing.invoiceStatus.pendente"), tone: "warn" },
  falhou: { label: i18n.t("billing.invoiceStatus.falhou"), tone: "error" },
  cancelada: { label: i18n.t("billing.invoiceStatus.cancelada"), tone: "muted" },
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = "#3b82f6";
  const accentRgb = "79, 124, 255";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#5a6478";
  const textFaint = isDark ? "#5a6478" : "#9aa3b2";
  const border = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  const surfaceCard = isDark ? "rgba(17, 19, 24, 0.85)" : "rgba(255, 255, 255, 0.95)";

  return {
    root: {
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
      display: "flex",
      flexDirection: "column",
      gap: 16,
      maxWidth: 720,
    },
    loading: { display: "flex", justifyContent: "center", padding: 48 },
    errorBox: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "16px 18px",
      borderRadius: 12,
      border: `1px solid ${border}`,
      background: isDark ? "rgba(239,68,68,0.06)" : "rgba(239,68,68,0.04)",
      color: textSecondary,
      fontSize: 13,
    },
    retryBtn: {
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: "transparent",
      color: textPrimary,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
    },
    manageBtn: {
      marginTop: 16,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "9px 14px",
      borderRadius: 10,
      border: "none",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      color: "#fff",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      "&:hover": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
    },
    cancelLink: {
      marginTop: 14,
      alignSelf: "flex-start",
      background: "transparent",
      border: "none",
      color: textFaint,
      cursor: "pointer",
      fontSize: 12.5,
      fontWeight: 500,
      textDecoration: "underline",
      padding: 0,
      "&:hover": { color: "#ef4444" },
    },
    cancelNotice: {
      marginTop: 14,
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${isDark ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.2)"}`,
      background: isDark ? "rgba(239,68,68,0.07)" : "rgba(239,68,68,0.04)",
      color: textSecondary,
      fontSize: 12.5,
      lineHeight: 1.45,
    },
    pixNotice: {
      marginTop: 14,
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${isDark ? "rgba(34,197,94,0.25)" : "rgba(34,197,94,0.2)"}`,
      background: isDark ? "rgba(34,197,94,0.07)" : "rgba(34,197,94,0.04)",
      color: textSecondary,
      fontSize: 12.5,
      lineHeight: 1.45,
    },
    dlg: {
      "& .MuiDialog-paper": {
        background: surfaceCard,
        borderRadius: 16,
        border: `1px solid ${border}`,
        maxWidth: 420,
        width: "calc(100vw - 48px)",
        fontFamily: "'Montserrat', sans-serif",
      },
    },
    dlgBody: { padding: 24, display: "flex", flexDirection: "column", gap: 12 },
    dlgTitle: { fontSize: 17, fontWeight: 700, color: textPrimary, display: "flex", alignItems: "center", gap: 8 },
    dlgText: { fontSize: 13.5, color: textSecondary, lineHeight: 1.5 },
    dlgActions: { display: "flex", gap: 10, marginTop: 8 },
    dlgKeep: { flex: 1, height: 42, borderRadius: 10, border: `1px solid ${border}`, background: "transparent", color: textPrimary, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" },
    dlgConfirm: { flex: 1, height: 42, borderRadius: 10, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, "&:disabled": { opacity: 0.6, cursor: "not-allowed" } },
    dlgErr: { fontSize: 12.5, color: "#ef4444" },
    pmRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    pmCard: {
      fontSize: 14.5,
      fontWeight: 600,
      color: textPrimary,
      letterSpacing: 0.3,
    },
    pmGhostBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: "transparent",
      color: textPrimary,
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      "&:hover": { borderColor: accent, background: isDark ? `rgba(${accentRgb},0.06)` : `rgba(${accentRgb},0.04)` },
    },
    card: {
      background: surfaceCard,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: 20,
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: textSecondary,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 16,
    },
    planHead: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    },
    planIconName: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    planIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      flexShrink: 0,
      background: `rgba(${accentRgb}, 0.12)`,
      color: accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    planName: { fontSize: 17, fontWeight: 700, color: textPrimary },
    planSub: { fontSize: 12.5, color: textSecondary, marginTop: 2 },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "5px 11px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
    tOk: { background: "rgba(34,197,94,0.12)", color: "#22c55e" },
    tWarn: { background: "rgba(245,158,11,0.14)", color: "#f59e0b" },
    tError: { background: "rgba(239,68,68,0.12)", color: "#ef4444" },
    tMuted: { background: "rgba(160,174,192,0.14)", color: textFaint },
    tInfo: { background: "rgba(59,130,246,0.12)", color: accent },
    planRows: {
      marginTop: 18,
      paddingTop: 16,
      borderTop: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    kv: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 16,
      fontSize: 13.5,
    },
    k: { color: textSecondary, flexShrink: 0 },
    v: { color: textPrimary, fontWeight: 600, textAlign: "right", wordBreak: "break-word" },
    bigV: { fontSize: 16, fontWeight: 700 },

    invoiceRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 0",
      borderBottom: `1px solid ${border}`,
      fontSize: 13.5,
    },
    invDate: { color: textPrimary, fontWeight: 600, minWidth: 96 },
    invPeriod: { color: textFaint, fontSize: 11.5, flex: 1, minWidth: 0 },
    invAmount: { color: textPrimary, fontWeight: 600, marginLeft: "auto" },
    empty: {
      padding: "24px 8px",
      textAlign: "center",
      color: textFaint,
      fontSize: 13,
    },
  };
});

const Badge = ({ classes, tone, children }) => {
  const map = { ok: classes.tOk, warn: classes.tWarn, error: classes.tError, muted: classes.tMuted, info: classes.tInfo };
  return <span className={`${classes.badge} ${map[tone] || classes.tMuted}`}>{children}</span>;
};

const Row = ({ classes, k, v, big }) => (
  <div className={classes.kv}>
    <span className={classes.k}>{k}</span>
    <span className={`${classes.v} ${big ? classes.bigV : ""}`}>{v}</span>
  </div>
);

// Renderiza strings traduzidas que contêm <strong>…</strong> como JSX (preserva o
// negrito sem dangerouslySetInnerHTML). Mantém o markup que já existia no componente.
const RichText = ({ text }) =>
  String(text)
    .split(/(<strong>.*?<\/strong>)/g)
    .filter((seg) => seg !== "")
    .map((seg, i) => {
      const m = seg.match(/^<strong>(.*?)<\/strong>$/);
      return m ? <strong key={i}>{m[1]}</strong> : <React.Fragment key={i}>{seg}</React.Fragment>;
    });

const BillingPanel = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState(null); // null=fechado | "attendance" | "internal"
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelErr, setCancelErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/account/billing");
      setData(data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    setCancelErr("");
    setCancelling(true);
    try {
      await api.post("/account/cancel");
      setCancelOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data;
      setCancelErr((d?.detail && (d.detail.message || (typeof d.detail === "string" ? d.detail : ""))) || d?.error || i18n.t("billing.cancelError"));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={classes.root}>
        <div className={classes.errorBox}>
          <AlertCircle size={18} />
          {i18n.t("billing.loadError")}
          <button className={classes.retryBtn} onClick={load}>
            <RefreshCw size={13} /> {i18n.t("billing.retry")}
          </button>
        </div>
      </div>
    );
  }

  // Conta CORTESIA/ISENTA — fora do fluxo de pagamento.
  if (data.exempt) {
    return (
      <div className={classes.root}>
        <div className={classes.card}>
          <div className={classes.planHead}>
            <div className={classes.planIconName}>
              <div className={classes.planIcon}><Sparkles size={20} /></div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.planName}>{i18n.t("billing.exempt.title")}</div>
                <div className={classes.planSub}>{i18n.t("billing.exempt.desc")}</div>
              </div>
            </div>
            <Badge classes={classes} tone="ok">{i18n.t("billing.exempt.badge")}</Badge>
          </div>
        </div>
      </div>
    );
  }

  const plan = data.plan;
  if (!data.order_id || !plan) {
    return (
      <div className={classes.root}>
        <div className={classes.card}>
          <div className={classes.empty}>{i18n.t("billing.noOrder")}</div>
        </div>
      </div>
    );
  }

  const sm = STATUS_META[plan.status] || { label: plan.status || "—", tone: "muted" };
  const planName = `${plan.produto || i18n.t("billing.productFallback")}${plan.tier_label ? " " + plan.tier_label : ""}`;
  const cicloLabel = plan.ciclo === "anual" ? i18n.t("billing.cycleAnnual") : i18n.t("billing.cycleMonthly");
  const perCycle = plan.ciclo === "anual" ? i18n.t("billing.perCycleAnnual") : i18n.t("billing.perCycleMonthly");
  const dleft = plan.status === "teste_gratis" ? daysLeft(plan.trial_end) : null;

  const pm = data.payment_method;
  const cardLine = pm && pm.last_four_digits
    ? `•••• ${pm.last_four_digits}${pm.brand ? " · " + cap(pm.brand) : ""}${pm.exp_month && pm.exp_year ? ` · ${String(pm.exp_month).padStart(2, "0")}/${pm.exp_year}` : ""}`
    : null;

  const b = data.billing || {};
  const addr = b.address;
  const addrLine = addr && (addr.line_1 || addr.city)
    ? `${addr.line_1 || ""}${addr.line_2 ? " · " + addr.line_2 : ""}${addr.city ? ` — ${addr.city}${addr.state ? "/" + addr.state : ""}` : ""}${addr.zip_code ? ` · ${i18n.t("billing.zipPrefix")} ${addr.zip_code}` : ""}`
    : null;

  const invoices = Array.isArray(data.invoices) ? data.invoices : [];

  return (
    <div className={classes.root}>
      {/* ── Plano atual ── */}
      <div className={classes.card}>
        <div className={classes.planHead}>
          <div className={classes.planIconName}>
            <div className={classes.planIcon}><Sparkles size={20} /></div>
            <div style={{ minWidth: 0 }}>
              <div className={classes.planName}>{planName}</div>
              <div className={classes.planSub}>
                {cicloLabel} · {plan.seats} {plan.seats === 1 ? i18n.t("billing.userSingular") : i18n.t("billing.userPlural")}
                {plan.addon_chat_interno ? i18n.t("billing.withInternalChat") : ""}
              </div>
            </div>
          </div>
          <Badge classes={classes} tone={sm.tone}>{sm.label}</Badge>
        </div>

        <div className={classes.planRows}>
          <Row classes={classes} k={i18n.t("billing.amount")} big v={`${fmtMoney(plan.valor_cents)} ${perCycle}`} />
          {plan.status === "teste_gratis" ? (
            <Row
              classes={classes}
              k={i18n.t("billing.trialEndsOn")}
              v={dleft != null ? `${fmtDate(plan.trial_end)} (${dleft} ${dleft === 1 ? i18n.t("billing.daySingular") : i18n.t("billing.dayPlural")})` : fmtDate(plan.trial_end)}
            />
          ) : (
            <Row classes={classes} k={i18n.t("billing.nextCharge")} v={fmtDate(plan.next_billing_at)} />
          )}
        </div>
        {/* Upgrades operam sobre a assinatura Pagar.me — só p/ cartão (PIX não tem). */}
        {plan.status === "pago_ativo" && plan.subscription_id && !plan.cancel_at_period_end && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className={classes.manageBtn} onClick={() => setUpgradeMode("attendance")}>
              <ArrowUpCircle size={15} /> {i18n.t("billing.manageUsers")}
            </button>
            <button className={classes.manageBtn} onClick={() => setUpgradeMode("internal")}>
              <ArrowUpCircle size={15} /> {i18n.t("billing.manageInternalChat")}
            </button>
          </div>
        )}
        {plan.cancel_at_period_end && (
          <div className={classes.cancelNotice}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1, color: "#ef4444" }} />
            <span>
              <RichText text={i18n.t("billing.cancelNotice", { date: fmtDate(plan.access_until) })} />
            </span>
          </div>
        )}
        {/* PIX anual (sem assinatura): mostra a validade do acesso pago. */}
        {plan.status === "pago_ativo" && !plan.subscription_id && plan.access_until && (
          <div className={classes.pixNotice}>
            <QrCode size={15} style={{ flexShrink: 0, marginTop: 1, color: "#22c55e" }} />
            <span>
              <RichText text={i18n.t("billing.pixNotice", { date: fmtDate(plan.access_until) })} />
            </span>
          </div>
        )}
        {/* Cancelar só faz sentido em assinatura recorrente (cartão). PIX não renova. */}
        {plan.status === "pago_ativo" && plan.subscription_id && !plan.cancel_at_period_end && (
          <button className={classes.cancelLink} onClick={() => { setCancelErr(""); setCancelOpen(true); }}>
            {i18n.t("billing.cancelSubscription")}
          </button>
        )}
      </div>

      {/* ── Forma de pagamento ── (só cartão; PIX anual não tem cartão salvo) */}
      {plan.status === "pago_ativo" && plan.subscription_id && (
        <div className={classes.card}>
          <div className={classes.sectionTitle}>
            <CreditCard size={15} /> {i18n.t("billing.paymentMethodTitle")}
          </div>
          <div className={classes.pmRow}>
            <span className={classes.pmCard}>{cardLine || i18n.t("billing.noCard")}</span>
            <button className={classes.pmGhostBtn} onClick={() => setCardModalOpen(true)}>
              <CreditCard size={14} /> {i18n.t("billing.changeCard")}
            </button>
          </div>
        </div>
      )}

      {/* ── Histórico de faturas ── */}
      <div className={classes.card}>
        <div className={classes.sectionTitle}>
          <ReceiptText size={15} /> {i18n.t("billing.invoicesTitle")}
        </div>
        {invoices.length === 0 ? (
          <div className={classes.empty}>{i18n.t("billing.noInvoices")}</div>
        ) : (
          invoices.map((inv) => {
            const im = INVOICE_META[inv.status] || { label: inv.status, tone: "muted" };
            return (
              <div key={inv.id} className={classes.invoiceRow}>
                <span className={classes.invDate}>{fmtDate(inv.paid_at || inv.created_at)}</span>
                <span className={classes.invPeriod}>
                  {inv.cycle_start ? `${fmtDate(inv.cycle_start)} – ${fmtDate(inv.cycle_end)}` : ""}
                </span>
                <span className={classes.invAmount}>{fmtMoney(inv.amount_cents)}</span>
                <Badge classes={classes} tone={im.tone}>{im.label}</Badge>
              </div>
            );
          })
        )}
      </div>

      {/* ── Dados de cobrança ── */}
      <div className={classes.card}>
        <div className={classes.sectionTitle}>
          <UserRound size={15} /> {i18n.t("billing.billingDataTitle")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Row classes={classes} k={i18n.t("billing.name")} v={b.name || "—"} />
          <Row classes={classes} k={i18n.t("billing.email")} v={b.email || "—"} />
          <Row classes={classes} k={i18n.t("billing.document")} v={b.document || "—"} />
          <Row classes={classes} k={i18n.t("billing.phone")} v={b.phone || "—"} />
          <Row
            classes={classes}
            k={i18n.t("billing.address")}
            v={addrLine || <span style={{ fontWeight: 400 }}>—</span>}
          />
        </div>
      </div>

      {/* E3b — upgrade proativo de assentos (recarrega o billing ao concluir). */}
      <UpgradeModal open={!!upgradeMode} mode={upgradeMode || "attendance"} onClose={() => setUpgradeMode(null)} onUpgraded={load} />
      {/* Trocar cartão (recarrega o billing p/ mostrar o novo cartão). */}
      <ChangeCardModal open={cardModalOpen} onClose={() => setCardModalOpen(false)} onChanged={load} />

      {/* Cancelar assinatura (no fim do período). */}
      <Dialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)} className={classes.dlg} maxWidth={false}>
        <div className={classes.dlgBody}>
          <div className={classes.dlgTitle}><Ban size={19} style={{ color: "#ef4444" }} /> {i18n.t("billing.cancelSubscription")}</div>
          <div className={classes.dlgText}>
            <RichText text={i18n.t("billing.cancelDialogText", { date: fmtDate(data?.plan?.next_billing_at) })} />
            {data?.plan?.tier === "pro" && i18n.t("billing.cancelDialogProAi")}
          </div>
          {cancelErr && <div className={classes.dlgErr}>{cancelErr}</div>}
          <div className={classes.dlgActions}>
            <button className={classes.dlgKeep} onClick={() => setCancelOpen(false)} disabled={cancelling}>{i18n.t("billing.keepSubscription")}</button>
            <button className={classes.dlgConfirm} onClick={handleCancel} disabled={cancelling}>
              {cancelling ? <Loader size={16} /> : <Ban size={16} />}
              {cancelling ? i18n.t("billing.cancelling") : i18n.t("billing.cancelSubscription")}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default BillingPanel;
